/**
 * The admin console route table.
 *
 * Simpler than the tenant app's: there is one audience (platform staff) and
 * one gate (AdminLayout), so no per-route permission guards are needed.
 *
 * Organizations are the console's subject. There is deliberately no user
 * route: organization users are viewed inside their organization, on the
 * organization detail screen, and there is no platform-wide user list to link
 * to — the API does not offer one.
 */
import { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import { AdminLayout } from '../layouts/AdminLayout.jsx';
import { EmptyState, LoadingState } from '../components/States.jsx';
import { LinkButton } from '../components/Button.jsx';
import { Icon } from '../components/Icon.jsx';
import { AdminLoginPage } from '../modules/auth/AdminLoginPage.jsx';

const page = (loader, name) => lazy(() => loader().then((module) => ({ default: module[name] })));

const OverviewPage = page(() => import('../modules/dashboard/OverviewPage.jsx'), 'OverviewPage');
const OrganizationListPage = page(
  () => import('../modules/organizations/OrganizationListPage.jsx'),
  'OrganizationListPage'
);
const OrganizationDetailPage = page(
  () => import('../modules/organizations/OrganizationDetailPage.jsx'),
  'OrganizationDetailPage'
);
const CreateOrganizationPage = page(
  () => import('../modules/organizations/CreateOrganizationPage.jsx'),
  'CreateOrganizationPage'
);
const ModulesPage = page(() => import('../modules/modules/ModulesPage.jsx'), 'ModulesPage');
const ModuleRequestsPage = page(
  () => import('../modules/module-requests/ModuleRequestsPage.jsx'),
  'ModuleRequestsPage'
);
const PlansPage = page(() => import('../modules/plans/PlansPage.jsx'), 'PlansPage');
const HealthPage = page(() => import('../modules/health/HealthPage.jsx'), 'HealthPage');

function NotFound() {
  return (
    <div className="card" style={{ marginTop: 'var(--s-8)' }}>
      <EmptyState
        icon="compass"
        title="Page not found"
        message="That link does not exist in the admin console. Search for what you wanted with Ctrl K, or start from the overview."
        action={
          <LinkButton to="/" variant="primary" icon={<Icon name="overview" size={15} />}>
            Back to the overview
          </LinkButton>
        }
      />
    </div>
  );
}

export function AdminRoutes() {
  return (
    <Suspense fallback={<LoadingState />}>
      <Routes>
        <Route path="/login" element={<AdminLoginPage />} />

        <Route element={<AdminLayout />}>
          <Route index element={<OverviewPage />} />
          <Route path="/organizations" element={<OrganizationListPage />} />
          {/* Before /organizations/:id, or "new" is read as an id. */}
          <Route path="/organizations/new" element={<CreateOrganizationPage />} />
          <Route path="/organizations/:id" element={<OrganizationDetailPage />} />
          <Route path="/modules" element={<ModulesPage />} />
          <Route path="/module-requests" element={<ModuleRequestsPage />} />
          <Route path="/plans" element={<PlansPage />} />
          <Route path="/health" element={<HealthPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
